import {BrowserModule} from '@angular/platform-browser';
import {NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {DatePipe} from '@angular/common';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {LoginComponent} from './login/login.component';
import {HttpInterceptorService} from './http-interceptor.service';
import {MenuComponent} from './menu/menu.component';
import {LogoutComponent} from './logout/logout.component';
import {NgxDatatableModule} from '@swimlane/ngx-datatable';
import {SecondarymenuComponent} from './secondarymenu/secondarymenu.component';
import {MsalGuard, MsalInterceptor, MsalModule, MsalRedirectComponent} from '@azure/msal-angular'; // Import MsalInterceptor
import {InteractionType, PublicClientApplication} from '@azure/msal-browser';
import {environment} from '../environments/environment';
import {SpinnerComponent} from './spinner/spinner.component';
import {RevenueComponent} from './revenue/revenue.component';
import {HidePipe} from './hide.pipe';
import {ToastrModule} from 'ngx-toastr';
import {SessionExpirationAlert} from './session-expiration-alert/session-expiration-alert.module';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatListModule} from '@angular/material/list';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatTabsModule} from '@angular/material/tabs';
import {MatPaginatorModule} from '@angular/material/paginator';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule} from '@angular/material/core';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatStepperModule} from '@angular/material/stepper';
import {MatTableModule} from '@angular/material/table';
import {MatInputModule} from '@angular/material/input';
import {MatDialogModule} from '@angular/material/dialog';
import {MatSelectModule} from '@angular/material/select';
import {MatSortModule} from '@angular/material/sort';
import {VersionDialog} from './menu/modals/version-dialog';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatChipsModule} from '@angular/material/chips';
import {MatBadgeModule} from '@angular/material/badge';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter} from '@angular/material-moment-adapter';
import {MatRadioModule} from '@angular/material/radio';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatExpansionModule} from '@angular/material/expansion';
import {MonitoringComponent} from './monitoring/monitoring.component';
import {AppSettingsComponent} from './app-settings/app-settings.component';
import {ToolsComponent} from './tools/tools.component';
import {LocationComponent} from './location/location.component';
import {ProviderComponent} from './provider/provider.component';
import {ActivateLocationDialog} from './location/modals/activate/activate-location-dialog';
import {JournalEntryComponent} from './journal-entry/journal-entry.component';
import {CreateLocationDialog} from './location/modals/create/create-location-dialog';
import {DeleteLocationDialog} from './location/modals/delete/delete-location-dialog';
import {ViewJobHistoryDialog} from './monitoring/modals/view/view-job-history-dialog';
import {DeleteJobHistoryDialog} from './monitoring/modals/delete/delete-job-history-dialog';

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

const MY_FORMATS = {
    parse: {
        dateInput: 'MM-DD-YYYY',
    },
    display: {
        dateInput: 'MM-DD-YYYY',
        monthYearLabel: 'YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'YYYY',
    },
};

@NgModule({
    declarations: [
        AppComponent,
        LoginComponent,
        MenuComponent,
        LogoutComponent,
        SecondarymenuComponent,
        SpinnerComponent,
        RevenueComponent,
        HidePipe,
        VersionDialog,
        MonitoringComponent,
        AppSettingsComponent,
        ToolsComponent,
        LocationComponent,
        ProviderComponent,
        ActivateLocationDialog,
        JournalEntryComponent,
        CreateLocationDialog,
        DeleteLocationDialog,
        ViewJobHistoryDialog,
        DeleteJobHistoryDialog
    ],
    imports: [
        BrowserModule,
        AppRoutingModule,
        FormsModule,
        HttpClientModule,
        NgxDatatableModule,
        ToastrModule.forRoot({
            closeButton: true,
            timeOut: 7000,
            positionClass: 'toast-top-right',
            newestOnTop: true,
            progressBar: true
        }),
        SessionExpirationAlert.forRoot({totalMinutes: 30}),
        MsalModule.forRoot(new PublicClientApplication({
            auth: environment.auth,
            cache: {
                cacheLocation: 'localStorage',
                storeAuthStateInCookie: isIE,
            }
        }), {
            interactionType: InteractionType.Redirect,
            authRequest: {
                scopes: ['user.read']
            }
        }, {
            interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
            protectedResourceMap: new Map([
                ['https://graph.microsoft.com/v1.0/me', ['user.read']]
            ])
        }),
        BrowserAnimationsModule,
        MatToolbarModule,
        MatSidenavModule,
        MatListModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatTabsModule,
        MatTableModule,
        MatPaginatorModule,
        MatProgressSpinnerModule,
        MatFormFieldModule,
        MatInputModule,
        MatDialogModule,
        MatRadioModule,
        ReactiveFormsModule,
        MatSelectModule,
        MatProgressBarModule,
        MatSnackBarModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatCheckboxModule,
        MatStepperModule,
        MatSortModule,
        MatButtonToggleModule,
        MatChipsModule,
        MatBadgeModule,
        MatTooltipModule,
        MatExpansionModule
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpInterceptorService,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]},
        {provide: MAT_DATE_FORMATS, useValue: MY_FORMATS},
        {
            provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: {useUtc: true}
        },
        MsalGuard,
        DatePipe
    ],
    bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule {
}
